import Header from "../header/header";
import Footer from "../footer/footer";
import NavBar from "../nav/nav-bar";
import ReportNav from "./report-nav";
import { VscActivateBreakpoints } from "react-icons/vsc";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useState, useEffect, useCallback, useMemo } from "react";
import { storage } from "../utils/storage";
import Service from "./../webservice/http";
import { useHistory } from "react-router-dom";
import ReportFilter from "./report-filter";
import { FaFilter } from "react-icons/fa";
import ReportFilterBanner from "./report-filter-banner";

const fmt = (symbol, val) =>
  `${symbol}${Number(val || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const pct = (target, achieved) =>
  target > 0 ? ((achieved / target) * 100).toFixed(1) : "0.0";

const badgeColor = (p) => {
  const v = parseFloat(p);
  if (v >= 80) return { bg: "#e8f9f1", color: "#1a9e5c", border: "#a3e4c3" };
  if (v >= 50) return { bg: "#fff8e6", color: "#c97d00", border: "#ffd980" };
  return { bg: "#fdecea", color: "#c0392b", border: "#f5b7b1" };
};

const buildCurrencyChartOptions = (chartData, currencyBreakdown = []) => {
  if (!chartData || !chartData.labels || chartData.labels.length === 0) {
    return {
      title: { text: "No data available" },
      chart: { type: "pie", height: 440 },
      series: [],
    };
  }

  const seriesData = chartData.labels.map((label, index) => {
    const breakdown = currencyBreakdown.find((b) => b.currency_code === label);
    const targetFmt =
      breakdown?.target_formatted ||
      fmt(breakdown?.currency_symbol || "", breakdown?.target || 0);
    const achievedFmt =
      breakdown?.collected_formatted ||
      fmt(breakdown?.currency_symbol || "", chartData.data[index] || 0);
    const pctVal = pct(breakdown?.target || 0, chartData.data[index] || 0);

    return {
      name: label,
      y: chartData.data[index],
      color: chartData.colors?.[index] || "#4e81db",
      custom: {
        target: targetFmt,
        achieved: achievedFmt,
        pct: pctVal,
      },
    };
  });

  return {
    chart: {
      type: "pie",
      style: { fontFamily: "inherit" },
      height: 440,
      margin: [10, 10, 10, 10],
    },
    title: { text: null },
    tooltip: {
      useHTML: true,
      pointFormat: `<div style="padding:4px 2px;line-height:1.7">
          <span style="font-size:13px;font-weight:700">{point.name}</span><br/>
          <span style="color:#4e81db">▪ Target:</span> <b>{point.custom.target}</b><br/>
          <span style="color:#f28f43">▪ Achieved:</span> <b>{point.custom.achieved}</b><br/>
          <span style="color:#555">▪ Collected:</span> <b>{point.custom.pct}%</b>
        </div>`,
    },
    credits: { enabled: false },
    legend: { enabled: false },
    plotOptions: {
      pie: {
        innerSize: "52%",
        allowPointSelect: true,
        cursor: "pointer",
        borderWidth: 2,
        borderColor: "#fff",
        dataLabels: {
          enabled: true,
          distance: 18,
          style: {
            fontSize: "12px",
            fontWeight: "700",
            textOutline: "none",
            color: "#2c3e50",
          },
          format: "<b>{point.name}</b><br/>{point.percentage:.1f}%",
        },
      },
    },
    colors: chartData.colors || ["#4e81db", "#f28f43", "#1aadce", "#2c58ce"],
    series: [
      {
        name: "Achieved",
        colorByPoint: true,
        data: seriesData,
      },
    ],
  };
};

const tableStyles = {
  wrapper: {
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    padding: "16px",
    height: "100%",
  },
  title: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#2c3e50",
    marginBottom: "14px",
    letterSpacing: "0.4px",
    textTransform: "uppercase",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0 6px",
    fontSize: "13px",
  },
  th: {
    padding: "6px 10px",
    color: "#95a5a6",
    fontWeight: "600",
    textAlign: "right",
    fontSize: "11px",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    borderBottom: "1px solid #f0f0f0",
  },
  thLeft: {
    padding: "6px 10px",
    color: "#95a5a6",
    fontWeight: "600",
    textAlign: "left",
    fontSize: "11px",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    borderBottom: "1px solid #f0f0f0",
  },
};

const InlineTable = ({ data }) => (
  <div>
    <table style={tableStyles.table}>
      <thead>
        <tr>
          <th style={tableStyles.thLeft}>Currency</th>

          <th style={tableStyles.th}>Collected</th>
          <th style={tableStyles.th}>Target</th>
          <th style={tableStyles.th}>% Collected</th>
        </tr>
      </thead>
      <tbody>
        {data?.map?.((row) => {
          const target = row.target || 0;
          const collected = row.collected || 0;
          const p = pct(target, collected);
          const bc = badgeColor(p);
          const sym = row.currency_symbol || "";
          return (
            <tr
              key={row.currency_code}
              style={{
                backgroundColor: "#fafbfc",
                borderRadius: "8px",
              }}
            >
              {/* Currency pill */}
              <td style={{ padding: "8px 10px" }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    fontWeight: "700",
                    color: "#2c3e50",
                  }}
                >
                  <span
                    style={{
                      backgroundColor: "#4e81db",
                      color: "#fff",
                      borderRadius: "6px",
                      padding: "2px 7px",
                      fontSize: "11px",
                      fontWeight: "700",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {row.currency_code}
                  </span>
                </span>
              </td>
              {/* Target */}
              <td
                style={{
                  padding: "8px 10px",
                  textAlign: "right",
                  color: "#f28f43",
                  fontWeight: "600",
                }}
              >
                {row.collected_formatted || fmt(sym, collected)}
              </td>
              <td
                style={{
                  padding: "8px 10px",
                  textAlign: "right",
                  color: "#4e81db",
                  fontWeight: "600",
                }}
              >
                {row.target_formatted || fmt(sym, target)}
              </td>
              {/* Achieved */}

              {/* % badge */}
              <td style={{ padding: "8px 10px", textAlign: "right" }}>
                <span
                  style={{
                    display: "inline-block",
                    padding: "3px 10px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "700",
                    background: bc.bg,
                    color: bc.color,
                    border: `1px solid ${bc.border}`,
                  }}
                >
                  {p}%
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>

    {/* Legend */}
    {/* <div
      style={{
        marginTop: "16px",
        display: "flex",
        gap: "16px",
        flexWrap: "wrap",
        borderTop: "1px solid #f0f0f0",
        paddingTop: "12px",
      }}
    >
      {[
        { color: "#1a9e5c", label: "≥ 80% — On Track" },
        { color: "#c97d00", label: "50–79% — Moderate" },
        { color: "#c0392b", label: "< 50% — Needs Attention" },
      ].map((l) => (
        <span
          key={l.label}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            fontSize: "11px",
            color: "#7f8c8d",
          }}
        >
          <span
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: l.color,
              display: "inline-block",
            }}
          />
          {l.label}
        </span>
      ))}
    </div> */}
  </div>
);

const Performance = () => {
  const history = useHistory();
  const services = useMemo(() => new Service(), []);
  const role = storage.getItem("role");
  const isAgentRole =
    role === "Agent" ||
    role === "Agenth" ||
    role === "Agent Collections" ||
    role.includes("Agent");

  const [showAgentView, setShowAgentView] = useState(true);
  const [showManagerView, setShowManagerView] = useState(false);
  const [performanceData, setPerformanceData] = useState(null);

  const [currencyBreakdown, setCurrencyBreakdown] = useState([]);
  const [overallPerformanceChart, setOverallPerformanceChart] = useState(null);

  const [reportFilterWidth, setReportFilterWidth] = useState(0);
  const [isReportFilterOpen, setIsReportFilterOpen] = useState(false);
  const [reportFilters, setReportFilters] = useState({});

  const changeReportFilterWidth = () => {
    if (reportFilterWidth === 550) {
      setReportFilterWidth(0);
      setIsReportFilterOpen(false);
    } else {
      setReportFilterWidth(550);
      setIsReportFilterOpen(true);
    }
  };
  const handleReportFilterApply = (f) => setReportFilters(f);
  const handleReportFilterReset = () => setReportFilters({});

  const buildQueryParams = (filters) => {
    const params = new URLSearchParams();
    const mapping = {
      lob: "lob_name",
      manager: "manager_name",
      team_lead: "team_lead_name",
      agent: "agent_name",
      dca_organization: "dca_organization_name",
      dca_manager: "dca_manager_name",
      dca_team: "dca_team_lead_name",
      dca_agent: "dca_agent_name",
      collection_strategy: "collection_strategy",
      product_type: "product_name",
      time_period: "period",
      from_date: "from_date",
      to_date: "to_date",
      bucket_type: "bucket_type",
    };
    for (const [key, value] of Object.entries(filters)) {
      if (mapping[key] && value) params.append(mapping[key], value);
      else if (value && !mapping[key]) params.append(key, value);
    }
    return params.toString().replace(/\+/g, "%20");
  };

  // const fetchPerformanceData = useCallback(async () => {
  //   try {
  //     const res = await services.get(`api/report/performance_analysis/`);
  //     if (!res || res === "TypeError: Failed to fetch") return;
  //     if (res.code === "token_not_valid") {
  //       storage.clear();
  //       history.push("/login");
  //       return;
  //     }
  //     setPerformanceData(res);
  //     // Fallback/sync if agent API implements similar breakdown shape
  //     if (res.currency_breakdown) setCurrencyBreakdown(res.currency_breakdown);
  //     if (res.overall_performance_chart)
  //       setOverallPerformanceChart(res.overall_performance_chart);
  //   } catch (e) {
  //     console.warn("Performance analysis API error:", e.message);
  //   }
  // }, [history, services]);

  const fetchHierarchyPerformance = useCallback(
    async (filters = {}) => {
      try {
        const qs = buildQueryParams(filters);
        const url = `api/call_verification/hierarchy-performance-analysis/${
          qs ? `?${qs}` : ""
        }`;
        const res = await services.get(url);
        if (!res || res === "TypeError: Failed to fetch") return;
        if (res.code === "token_not_valid") {
          storage.clear();
          history.push("/login");
          return;
        }
        const tv = res.target_vs_achieved || {};
        const targetVal = tv.target?.value ?? 0;
        const achievedVal = tv.achieved?.value ?? 0;
        setPerformanceData({
          chart_data: {
            target: targetVal,
            achieved: achievedVal,
            remaining: tv.gap?.value ?? targetVal - achievedVal,
            target_in_millions:
              tv.target?.indian_format ??
              tv.target?.formatted ??
              `${targetVal}`,
            achieved_in_millions:
              tv.achieved?.indian_format ??
              tv.achieved?.formatted ??
              `${achievedVal}`,
          },
          agents: res.agents || [],
        });
        setCurrencyBreakdown(res.currency_breakdown || []);
        setOverallPerformanceChart(res.overall_performance_chart || null);
      } catch (e) {
        console.warn("Hierarchy performance API error:", e.message);
      }
    },
    [history, services]
  );

  useEffect(() => {
    fetchHierarchyPerformance();
  }, [fetchHierarchyPerformance]);

  useEffect(() => {
    if (!isAgentRole) fetchHierarchyPerformance(reportFilters);
  }, [reportFilters]);

  const managerAgents = performanceData?.agents || [];

  const ManagerPerformanceOption = {
    chart: { type: "column", style: { fontFamily: "inherit" } },
    title: { text: "Agents' Performance" },
    xAxis: {
      categories: managerAgents.map((a) => a.name),
      crosshair: true,
    },
    yAxis: {
      min: 0,
      title: { text: "Values" },
      gridLineColor: "transparent",
    },
    tooltip: {
      headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
      pointFormat:
        '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
        '<td style="padding:0"><b>{point.y:.2f}</b></td></tr>',
      footerFormat: "</table>",
      shared: true,
      useHTML: true,
    },
    credits: { enabled: false },
    plotOptions: {
      column: { pointPadding: 0.2, borderWidth: 0, borderRadius: 4 },
      series: {
        events: {
          click: () => {
            setShowManagerView(false);
            setShowAgentView(true);
          },
        },
      },
    },
    series: [
      {
        name: "Target",
        color: "#4e81db",
        data: managerAgents.map((a) =>
          parseFloat((a.target?.value ?? 0).toFixed(2))
        ),
      },
      {
        name: "Achieved",
        color: "#f28f43",
        data: managerAgents.map((a) =>
          parseFloat((a.achieved?.value ?? 0).toFixed(2))
        ),
      },
    ],
  };

  const showAgentViewReport = () => {
    setShowManagerView(false);
    setShowAgentView(true);
  };
  const showManagerViewReport = () => {
    setShowManagerView(true);
    setShowAgentView(false);
  };

  const currencyChartOptions = buildCurrencyChartOptions(
    overallPerformanceChart,
    currencyBreakdown
  );

  return (
    <div className="dashboard-body">
      <Header />
      <NavBar />
      <div>
        <div className="container-fluid">
          <div className="my-call">
            <div
              className="side-panel-with-table clearfix"
              style={{ display: "flex" }}
            >
              <ReportNav />

              <div className="call-table" style={{ padding: "2px 10px" }}>
                {!isAgentRole && (
                  <ReportFilterBanner
                    filters={reportFilters}
                    onClear={handleReportFilterReset}
                  />
                )}

                <div className="my-calls-column">
                  <div className="calls-top-pannel">
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="pannel-heading clearfix">
                          <div className="pannel-heading-icon">
                            <VscActivateBreakpoints />
                          </div>
                          <div className="pannel-heading-info">
                            <h3>Performance</h3>
                            <p>Performance Analysis</p>
                          </div>
                        </div>
                      </div>
                      {storage.getItem("role").includes("Master") && (
                        <div className="col-lg-6">
                          <div className="pannel-nav clearfix">
                            <ul className="clearfix">
                              <div
                                className="btn-group btn-group-toggle"
                                data-toggle="buttons"
                              >
                                <label
                                  className={`btn btn-default ${
                                    showAgentView ? "active" : ""
                                  }`}
                                  id="7"
                                  onClick={showAgentViewReport}
                                >
                                  <input
                                    type="radio"
                                    name="options"
                                    autoComplete="off"
                                  />{" "}
                                  Self
                                </label>

                                <label
                                  className={`btn btn-default ${
                                    showManagerView ? "active" : ""
                                  }`}
                                  id="30"
                                  onClick={showManagerViewReport}
                                >
                                  <input
                                    type="radio"
                                    name="options"
                                    autoComplete="off"
                                  />{" "}
                                  Agents{" "}
                                </label>
                              </div>
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row" style={{ marginTop: "10px" }}>
                  {showManagerView && (
                    <div className="col-md-12">
                      <div
                        style={{
                          background: "#fff",
                          borderRadius: "12px",
                          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                          padding: "16px",
                          // marginBottom: "18px",
                        }}
                      >
                        <HighchartsReact
                          highcharts={Highcharts}
                          options={ManagerPerformanceOption}
                        />
                        <p
                          style={{
                            marginTop: "8px",
                            fontSize: "12px",
                            color: "#95a5a6",
                            textAlign: "center",
                          }}
                        >
                          * Values shown in the currency associated with each
                          agent's LOB.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* ── Agent / Overall view — single unified card ──── */}
                  {showAgentView && (
                    <div
                      className="col-md-12"
                      // style={{ marginBottom: "18px" }}
                    >
                      <div
                        style={{
                          background: "#fff",
                          borderRadius: "12px",
                          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                          padding: "20px 24px",
                          display: "flex",
                          alignItems: "stretch",
                          gap: "0",
                        }}
                      >
                        {/* ── Left: Donut chart ── */}
                        <div style={{ flex: "0 0 42%", minWidth: 0 }}>
                          {/* Card header */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              marginBottom: "4px",
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontSize: "15px",
                                  fontWeight: "700",
                                  color: "#2c3e50",
                                }}
                              >
                                {isAgentRole
                                  ? "Self Performance"
                                  : "Overall Performance"}
                              </div>
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#95a5a6",
                                  marginTop: "1px",
                                }}
                              >
                                Achieved share by currency
                              </div>
                            </div>
                          </div>

                          <HighchartsReact
                            highcharts={Highcharts}
                            options={currencyChartOptions}
                          />
                        </div>

                        {/* ── Divider ── */}
                        <div
                          style={{
                            width: "1px",
                            background: "#f0f0f0",
                            margin: "0 24px",
                            flexShrink: 0,
                          }}
                        />

                        {/* ── Right: Currency table ── */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "700",
                              color: "#2c3e50",
                              marginBottom: "14px",
                              letterSpacing: "0.4px",
                              textTransform: "uppercase",
                            }}
                          >
                            Currency Breakdown
                          </div>
                          <InlineTable data={currencyBreakdown} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* ─────────────────────────────────────────────────────── */}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      {/* Floating ReportFilter — non-agent roles only */}
      {!isAgentRole && (
        <>
          <button
            onClick={changeReportFilterWidth}
            title="Global Filters"
            style={{
              position: "fixed",
              transition: "0.5s",
              top: "30%",
              right: reportFilterWidth > 0 ? reportFilterWidth + 8 : 25,
              backgroundColor:
                Object.keys(reportFilters).length > 0 ? "#271078" : "#F99E52",
              color: "white",
              padding: "10px",
              height: "40px",
              width: "40px",
              zIndex: 2001,
              borderRadius: "10%",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
            }}
          >
            <FaFilter size={18} />
            {Object.keys(reportFilters).length > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: "-4px",
                  right: "-4px",
                  backgroundColor: "#F99E52",
                  color: "#fff",
                  borderRadius: "50%",
                  width: "18px",
                  height: "18px",
                  fontSize: "10px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {Object.keys(reportFilters).length}
              </span>
            )}
          </button>

          <ReportFilter
            width={reportFilterWidth}
            changeSideBarWidth={changeReportFilterWidth}
            onApplyFilter={handleReportFilterApply}
            onResetFilter={handleReportFilterReset}
            userRole={role}
          />

          {isReportFilterOpen && (
            <div
              onClick={changeReportFilterWidth}
              style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(0,0,0,0.35)",
                zIndex: 1999,
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default Performance;
